






























































import { PaginationQueryStringMixin } from '@/features/core/components/mixins/pagination-query-string';
import { EneriqTypesMixin } from '@/features/app-heating-system/components/mixins/measurements/eneriq-types-mixin';
import { Action, RootAction } from '@/features/core/store';
import { AddToastMessageParams } from '@/features/core/store/toast';
import { BASE_COLUMNS, HeatingSystemSummary } from '@/features/domain-ui/heating-system-list-table/model';
import { StringProp } from '@/util/prop-decorators';
import { isString } from 'lodash';
import { Component, Mixins, Watch } from 'vue-property-decorator';

import query from '@/hsc-api/queries/HeatingSystemCollectionWithPaginationQuery.gql';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import {
  HeatingSystemCollectionWithPaginationQuery,
  HeatingSystemCollectionWithPaginationQueryVariables,
} from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';
import { HeatingSystemHealth } from '@/types/iot-portal';
import alertHeatingSystemSummariesQuery from '@/hsc-api/queries/AlertTicketsSummaryByHeatingSystemIdQuery.gql';
import {
  AlertHeatingSystemSummaries,
  AlertHeatingSystemSummariesVariables,
  AlertHeatingSystemSummaries_statuses,
} from '@/hsc-api/queries/__generated__/AlertHeatingSystemSummaries';
import { Option } from '@/features/ui/inputs/model';
import HeatingSystemsMetadataModal from './HeatingSystemsMetadataModal.vue';
import GroupsMixin from '@/features/core/components/mixins/groups';
import HeatingSystemsAdvancedView from './HeatingSystemsAdvancedView.vue';
import { EnerIQSuggestionList, getEnerIQSeverityColor, SeverityColor } from '@/util/heating-systems';
import HeatingSystemListEditModal from './HeatingSystemListEditModal.vue';

type HeatingSystem = HeatingSystemCollectionWithPaginationQuery['heatingSystemsList']['items'][number];
type FilterStatus = 'ALL' | HeatingSystemHealth.CRITICAL | HeatingSystemHealth.DEGRADED;

interface EiqMessage {
  color: SeverityColor;
  message: string;
}

@Component({
  mixins: [GroupsMixin],
  components: {
    HeatingSystemsMetadataModal,
    HeatingSystemsAdvancedView,
    HeatingSystemListEditModal,
  },
  apollo: {
    heatingSystemsList: {
      query,
      client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
      fetchPolicy: 'no-cache',
      pollInterval: 60000,
      variables(this: HeatingSystemsListView): HeatingSystemCollectionWithPaginationQueryVariables {
        return {
          customerIdOrSiteId: this.treeNodeId,
          skip: this.skip,
          take: this.take,
          searchQuery: this.searchQuery === '' ? null : this.searchQuery,
        };
      },
      async result(this: HeatingSystemsListView): Promise<void> {
        this.searchQuery = this.nextSearchQuery;
        // Filter out items with empty heatingSystemMeasurementGroups
        const filteredHeatingSystems = this.heatingSystems.filter(
          (system) => system.heatingSystemMeasurementGroups.length > 0,
        );
        const data = await this.heatingSystemSummary(filteredHeatingSystems);
        this.summaries =
          this.selectedStatus === 'ALL' ? data : data.filter((item) => item.health === this.selectedStatus);
        this.triggerMetadataModal();
      },
    },
  },
  data(this: HeatingSystemsListView) {
    return {
      hiddenColumns: [],
      selectedIdForOverlay: undefined,
      searchQuery: isString(this.$route.query.query) ? this.$route.query.query : '',
      summaries: [],
      isSummariesLoaded: false,
    };
  },
})
export default class HeatingSystemsListView extends Mixins(PaginationQueryStringMixin, EneriqTypesMixin) {
  // Variables in data
  private summaries?: HeatingSystemSummary[];
  private searchQuery!: string;
  private isSummariesLoaded = false;
  private selectedIdForOverlay?: string;
  private selectedStatus: FilterStatus = 'ALL';
  private summariesLoading = false;
  private enerIQLoading = false;

  @StringProp(true)
  private readonly treeNodeId!: string;

  @RootAction
  private readonly ADD_TOAST_MESSAGES!: Action<AddToastMessageParams, void>;

  private readonly heatingSystemsList?: HeatingSystemCollectionWithPaginationQuery['heatingSystemsList'];

  private hiddenColumns!: string[];

  private statusOptions: Option[] = [
    { value: 'ALL', label: 'Alle Meldungen' },
    { value: HeatingSystemHealth.CRITICAL, label: 'Kritische Meldungen' },
    { value: HeatingSystemHealth.DEGRADED, label: 'Warnende Meldungen' },
  ];

  private readonly BASE_COLUMNS = BASE_COLUMNS;
  private heatingSystemSummariesQuery!: Promise<HeatingSystemSummary[]>;
  private expandedRows: number[] = [];
  // Watch for changes to summaries, like when the load function is called
  @Watch('$route', { immediate: true })
  onRouteChange(): void {
    this.showAdvancedView = this.$route.name === 'AppHeatingSystem/AdvancedView';
  }
  @Watch('summaries')
  private onSummariesChanged(newVal: HeatingSystemSummary[]): void {
    this.isSummariesLoaded = !!newVal;
  }

  private get heatingSystems(): HeatingSystem[] {
    return this.heatingSystemsList?.items ?? [];
  }
  protected get count(): number {
    return this.heatingSystemsList?.count ?? 0;
  }

  private get nextSearchQuery(): string {
    return isString(this.$route.query.query) ? this.$route.query.query : '';
  }

  private set nextSearchQuery(value: string) {
    this.$router.replace({ query: { ...this.$route.query, query: value === '' ? undefined : value } });

    if (!this.$apollo.queries.heatingSystemsList.loading) {
      this.searchQuery = value;
    }
  }

  private get loading(): boolean {
    return this.$apollo.queries.heatingSystemsList.loading || this.summariesLoading || this.enerIQLoading;
  }

  private get showAdvancedView(): boolean {
    return this.$store.getters.showAdvancedView;
  }

  private set showAdvancedView(value: boolean) {
    this.$store.commit('showAdvancedView', value);
  }

  private toggleRowExpansion(rowIndex: number): void {
    if (this.isRowExpanded(rowIndex)) {
      this.expandedRows = this.expandedRows.filter((currentRowIndex) => currentRowIndex !== rowIndex);
    } else {
      this.expandedRows.push(rowIndex);
    }
  }

  private isRowExpanded(rowIndex: number): boolean {
    return this.expandedRows.includes(rowIndex);
  }

  private async fetchAlertSummariesByHeatingSystem(
    heatingSystems: HeatingSystem[],
  ): Promise<AlertHeatingSystemSummaries_statuses[] | []> {
    try {
      this.summariesLoading = true;
      const heatingSystemIds = heatingSystems.map((hs) => hs.id);

      const variables: AlertHeatingSystemSummariesVariables = { hscIds: heatingSystemIds };
      const { data: alertTicketsData } = await this.$apollo.query<AlertHeatingSystemSummaries>({
        query: alertHeatingSystemSummariesQuery,
        client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
        variables,
        fetchPolicy: 'no-cache',
      });

      return alertTicketsData?.statuses || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return [];
    } finally {
      this.summariesLoading = false;
    }
  }

  private async fetchEnerIQSuggestions(ids: string | string[]): Promise<EnerIQSuggestionList[] | null> {
    const payload = Array.isArray(ids) ? ids.join(',') : ids;
    this.enerIQLoading = true;
    try {
      const res = await this.$store.dispatch('getLatestEnerIQSuggestion', payload);
      if (!res.ok) {
        return null;
      }

      return await res.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    } finally {
      this.enerIQLoading = false;
    }
  }

  private findHeatingSystemBySpotId(data: HeatingSystemSummary[], spotId: string): HeatingSystemSummary | null {
    for (const hs of data) {
      const measurementGroups = hs.components?.heatingSystemMeasurementGroups;
      if (!measurementGroups) continue;
      for (const group of measurementGroups) {
        const measurements = group.heatingSystemMeasurements;
        const foundMeasurement = measurements.find((measurement) => measurement.spotId === spotId);
        if (foundMeasurement) {
          return hs;
        }
      }
    }
    return null;
  }

  private async attachEnerIQData(heatingSystemSummary: HeatingSystemSummary[]): Promise<HeatingSystemSummary[]> {
    const spotIdsAndGroupNames = heatingSystemSummary.flatMap((item) =>
      item.components.heatingSystemMeasurementGroups.flatMap((i) =>
        i.heatingSystemMeasurements.flatMap((b) => ({ spotId: b.spotId, groupName: i.groupName })),
      ),
    );
    const uniqueSpotIdsAndGroupNames: Record<string, string> = spotIdsAndGroupNames.reduce((acc, item) => {
      acc[item.spotId] = item.groupName;
      return acc;
    }, {} as Record<string, string>);
    const uniqueSpotIds = Object.keys(uniqueSpotIdsAndGroupNames);
    const suggestionLists = await this.fetchEnerIQSuggestions(uniqueSpotIds);

    suggestionLists?.forEach((suggestionList) => {
      const hs = this.findHeatingSystemBySpotId(heatingSystemSummary, suggestionList.spotId);
      const { operations } = suggestionList.suggestion.score.metadata.reason;
      const messages: EiqMessage[] = Object.values(operations)
        .filter(
          (operation) =>
            ![SeverityColor.GREEN, SeverityColor.GRAY].includes(getEnerIQSeverityColor(operation.severity)) &&
            !['missing_sensor_id', 'received_empty_dataframe'].includes(operation.status_code),
        )
        .map((operation) => {
          const message = typeof operation.description === 'object' ? operation.description.de : operation.description;
          return {
            color: getEnerIQSeverityColor(operation.severity),
            message: `${uniqueSpotIdsAndGroupNames[suggestionList.spotId]}: ${message}`,
          };
        });
      if (hs) {
        const eiqMessages = messages.map((message) => message.message);
        hs.health = this.getHealthStatus(hs, messages);
        hs.statuses = hs.statuses && hs.statuses.length > 0 ? [...hs.statuses, ...eiqMessages] : [...eiqMessages];
      }
    });

    return heatingSystemSummary;
  }

  private getHealthStatus(heatingSystem: HeatingSystemSummary, eiqMessages: EiqMessage[]): string | undefined {
    if (heatingSystem.statuses?.length === 0 && eiqMessages.length === 0) {
      return HeatingSystemHealth.OK;
    } else if (heatingSystem.statuses?.length === 0 && eiqMessages.length > 0) {
      const hasCriticalEiqMessage = eiqMessages.find((message) => message.color === SeverityColor.RED);
      return hasCriticalEiqMessage ? HeatingSystemHealth.CRITICAL : HeatingSystemHealth.DEGRADED;
    }

    return heatingSystem.health;
  }

  private async heatingSystemSummary(heatingSystems: HeatingSystem[]): Promise<HeatingSystemSummary[]> {
    const alertSummarries = await this.fetchAlertSummariesByHeatingSystem(heatingSystems);
    const summaries: HeatingSystemSummary[] = [];
    for (const item of heatingSystems) {
      const status = alertSummarries.find((alert) => alert.id == item.id);
      const summary: HeatingSystemSummary = {
        name: item.name,
        health: status?.health ?? HeatingSystemHealth.OK,
        statusText: status?.statusText ?? '',
        statuses: status?.statuses ?? [],
        street: item.street,
        heatingType: item.heatingType,
        boilerDevice: item.boilerDevice,
        boilerModel: item.boilerModel,
        productSerial: item.productSerial,
        manufacturer: item.manufacturer,
        boilerManufacturer: item.boilerManufacturer,
        energyOutputRange: item.energyOutputRange,
        waterStorageType: item.waterStorageType,
        waterStorageModel: item.waterStorageModel,
        waterStorageManufacturer: item.waterStorageManufacturer,
        waterStorageCapacity: item.waterStorageCapacity,
        maintenanceCompany: item.maintenanceCompany,
        numApartmentUnits: item.numCommercialUnits,
        numCommercialUnits: item.numCommercialUnits,
        suppliedQuantity: item.suppliedQuantity,
        lastHydraulicBalancing: item.lastHydraulicBalancing,
        consumptions: item.heatingSystemConsumptions,
        components: item,
        siteId: item.siteId,
      };

      summaries.push(summary);
    }

    if (summaries.length > 0) {
      await this.attachEnerIQData(summaries);
    }

    return summaries;
  }

  private onUpdateStatus(value: FilterStatus): void {
    this.summaries = [];
    this.selectedStatus = value === null ? 'ALL' : value;
    this.$apollo.queries.heatingSystemsList.refetch();
  }

  private openModal(): void {
    this.$store.commit('openModal', true);
  }

  private openMetricsEditModal(): void {
    this.$store.commit('showEditMetricsModal', true);
  }

  private openAdvancedView(): void {
    this.showAdvancedView = !this.showAdvancedView;
  }

  private triggerMetadataModal(): void {
    if (this.$route.query['open-metadata-modal'] && this.$route.query['open-metadata-modal'] === 'true') {
      this.$store.commit('openModal', true);
    }
  }
}
